import './FvtLogin.scss';

import { FormEvent, useState } from 'react';
import { Breadcrumb, Container } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router';
import { faClock, faPaperPlane } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@myome/ui';

import { Turnstile } from '@components/Turnstile/Turnstile';
import { ROUTES } from '@constants/routes';
import InputField from '@pages/order/components/common/InputField';
import { requestFVTReferralSignInToken } from '@store/actions/referrals';

import { getFvtLoginEmailError } from './FvtLogin.validation';

export const FvtLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    const error = getFvtLoginEmailError(email);
    if (error) {
      setEmailError(error);
      setIsSubmitting(false);
      return;
    }

    setEmailError(undefined);
    try {
      await requestFVTReferralSignInToken(email);
      setSubmitted(true);
    } catch {
      setEmailError('We could not send a sign-in link. Please try again in a moment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onContinue = () => {
    navigate(ROUTES.LOGIN);
  };

  const onTryAgain = () => {
    setSubmitted(false);
    setEmailError(undefined);
  };

  return (
    <div className='fvt-login-page pt-4 w-100'>
      <Container fluid='xxl' className='fvt-login-page__breadcrumb mb-4'>
        <nav aria-label='Breadcrumb'>
          <Breadcrumb listProps={{ className: 'mb-0' }}>
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: ROUTES.FVT_WELCOME }}>
              Family Cascade Genetic Testing
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Sign in to invite relatives</Breadcrumb.Item>
          </Breadcrumb>
        </nav>
      </Container>

      <Container fluid='xxl' as='main' className='fvt-login-page__main mb-0'>
        <div className='fvt-login-page__main-inner'>
          <div className='fvt-login-page__card'>
            {!submitted ? (
              <form onSubmit={onSubmit} noValidate>
                <h1 className='fvt-login-page__title mb-3'>Sign in to send invitations</h1>
                <p className='fvt-login-page__lead mb-4'>
                  Enter the email address where you received your invitation from Myome. We&apos;ll send a one-time
                  sign-in link to that inbox — no password needed. Opening the link will bring you right back here to
                  invite your relatives.
                </p>
                <div className='mb-4'>
                  <InputField
                    id='fvt-login-email'
                    label='Email Address'
                    required
                    type='email'
                    autoComplete='email'
                    placeholder='jane.doe@example.com'
                    value={email}
                    showError={!!emailError}
                    error={emailError}
                    phiSanitize
                    onChange={value => {
                      setEmail(value);
                      if (emailError) setEmailError(undefined);
                    }}
                  />
                </div>
                <Turnstile execution='execute' />
                <Button
                  className='w-100'
                  type='submit'
                  variant='primary'
                  endIcon={<FontAwesomeIcon icon={faPaperPlane} />}
                  disabled={isSubmitting}
                >
                  Email me a sign-in link
                </Button>
              </form>
            ) : (
              <div>
                <h1 className='fvt-login-page__success-title mb-3'>Check your email</h1>
                <p className='fvt-login-page__success-body mb-3'>
                  If an account exists for that address, a sign-in link is on its way. The link expires in 1 hour.
                </p>
                <div className='fvt-login-page__continue-wrap pt-2'>
                  <p className='fvt-login-page__continue-hint mb-4'>
                    Didn&apos;t get it? Check spam, or{' '}
                    <button
                      type='button'
                      className='btn btn-link p-0 text-primary fs-sm text-decoration-underline fw-normal border-0 align-baseline'
                      onClick={onTryAgain}
                    >
                      try again
                    </button>
                    .
                  </p>

                  <Button className='w-100' type='button' variant='secondary' onClick={onContinue}>
                    Continue
                  </Button>
                </div>
              </div>
            )}
          </div>

          {!submitted && (
            <ul className='fvt-login-page__trust mt-4 mx-auto' aria-label='Sign-in assurances'>
              <li>
                <FontAwesomeIcon icon={faClock} aria-hidden='true' />
                Link expires in 1 hour
              </li>
            </ul>
          )}
        </div>
      </Container>
    </div>
  );
};
